import dayjs from "dayjs";
import {
  byDayThenName,
  endOfReviewWindow,
  eventMidday,
  formatDate,
  type DateFormat,
  type DocumentReview,
  type TimeDomain,
} from "../helpers";
import { type AnnualWheelEvent } from "../types";

export interface TimelineTick {
  date: Date;
  label: string;
}

// The row a label along a line sits in, and whether it runs back from its pin
export interface EventLabelPlace {
  row: number;
  isFlipped: boolean;
}

type TickUnit = "day" | "week" | "month" | "year";

interface TickStep {
  unit: TickUnit;
  every: number;
  days: number;
}

const MS_PER_DAY = 86_400_000;
const MS_PER_YEAR = 365 * MS_PER_DAY;
// The horizon is the stretch of time the timeline shows: from two weeks to ten years
const MIN_HORIZON_SPAN = 14 * MS_PER_DAY;
const MAX_HORIZON_SPAN = 10 * MS_PER_YEAR;
// A document's history gets some air on each side, and at least a year to sit in
const HISTORY_PADDING = 0.05;
const MIN_HISTORY_SPAN = MS_PER_YEAR;
// The room a tick label needs, in pixels; the widest ones name their year
const DATE_TICK_SPACING = 76;
const MONTH_TICK_SPACING = 56;
const WIDEST_TICK_STEP: TickStep = { unit: "year", every: 5, days: 1826 };
const TICK_STEPS: TickStep[] = [
  { unit: "day", every: 1, days: 1 },
  { unit: "day", every: 2, days: 2 },
  { unit: "week", every: 1, days: 7 },
  { unit: "week", every: 2, days: 14 },
  { unit: "month", every: 1, days: 30 },
  { unit: "month", every: 3, days: 91 },
  { unit: "year", every: 1, days: 365 },
  { unit: "year", every: 2, days: 730 },
  WIDEST_TICK_STEP,
];

export const HORIZON_ZOOM_STEP = 2;

export const isSameDomain = (a: TimeDomain, b: TimeDomain) =>
  a[0].getTime() === b[0].getTime() && a[1].getTime() === b[1].getTime();

const spanOf = ([start, end]: TimeDomain) => end.getTime() - start.getTime();

// A factor below 1 zooms in; the anchor, a fraction of the horizon, keeps its place
export const zoomHorizon = (
  horizon: TimeDomain,
  factor: number,
  anchor: number,
): TimeDomain => {
  const span = spanOf(horizon);
  const zoomedSpan = Math.min(
    Math.max(span * factor, MIN_HORIZON_SPAN),
    MAX_HORIZON_SPAN,
  );
  const start = horizon[0].getTime() + anchor * (span - zoomedSpan);

  return [new Date(start), new Date(start + zoomedSpan)];
};

// Moves the horizon by a fraction of its own length; positive is later
export const panHorizon = (
  [start, end]: TimeDomain,
  fraction: number,
): TimeDomain => {
  const shift = fraction * (end.getTime() - start.getTime());

  return [new Date(start.getTime() + shift), new Date(end.getTime() + shift)];
};

// The horizon that shows all of these reviews, as far as the widest zoom allows
export const historyHorizon = (
  documentReviews: DocumentReview[],
): TimeDomain | undefined => {
  if (documentReviews.length === 0) return undefined;

  const start = Math.min(
    ...documentReviews.map(({ reviewFrom }) => reviewFrom.getTime()),
  );
  const end = Math.max(
    ...documentReviews.map(({ reviewTo }) =>
      endOfReviewWindow(reviewTo).getTime(),
    ),
  );
  const paddedSpan = (end - start) * (1 + 2 * HISTORY_PADDING);
  const span = Math.min(
    Math.max(paddedSpan, MIN_HISTORY_SPAN),
    MAX_HORIZON_SPAN,
  );
  const middle = (start + end) / 2;

  return [new Date(middle - span / 2), new Date(middle + span / 2)];
};

// Whole days since 1970 in local time, which daylight saving cannot shift
const dayNumber = (date: dayjs.Dayjs) =>
  Date.UTC(date.year(), date.month(), date.date()) / MS_PER_DAY;

// Counted from a fixed point, so the ticks stay where they are while the horizon moves
const alignedTick = (date: Date, { unit, every }: TickStep) => {
  const day = dayjs(date).startOf("day");

  if (unit === "day") return day.subtract(dayNumber(day) % every, "day");

  if (unit === "week") {
    const monday = day.subtract((day.day() + 6) % 7, "day");

    // 5 January 1970, day number 4, was a Monday
    return monday.subtract(((dayNumber(monday) - 4) / 7) % every, "week");
  }

  if (unit === "month") {
    return day
      .startOf("month")
      .subtract((day.year() * 12 + day.month()) % every, "month");
  }

  return day.startOf("year").subtract(day.year() % every, "year");
};

// Nothing else on the timeline says which year it shows, so the first tick in
// view and the first one of every year name theirs
const tickFormat = (
  date: Date,
  unit: TickUnit,
  namesYear: boolean,
): DateFormat => {
  if (unit === "day" || unit === "week") return namesYear ? "date" : "dayMonth";

  const isYearStart = unit === "year" || date.getMonth() === 0;
  if (isYearStart) return "year";

  return namesYear ? "monthYear" : "month";
};

// Uses the finest step whose labels still fit in the width
export const timelineTicks = (domain: TimeDomain, width: number) => {
  const [start, end] = domain;
  const days = spanOf(domain) / MS_PER_DAY;
  const step =
    TICK_STEPS.find(({ unit, days: stepDays }) => {
      const spacing =
        unit === "day" || unit === "week"
          ? DATE_TICK_SPACING
          : MONTH_TICK_SPACING;

      return (width * stepDays) / days >= spacing;
    }) ?? WIDEST_TICK_STEP;
  const ticks: TimelineTick[] = [];
  let namedYear: number | undefined;

  for (
    let tick = alignedTick(start, step);
    tick.isBefore(end);
    tick = tick.add(step.every, step.unit)
  ) {
    if (tick.isBefore(start)) continue;

    const date = tick.toDate();
    const namesYear = date.getFullYear() !== namedYear;

    namedYear = date.getFullYear();
    ticks.push({
      date,
      label: formatDate(date, tickFormat(date, step.unit, namesYear)),
    });
  }

  return ticks;
};

// From the last label back to the first, each takes the lowest row with room for
// it, and goes without one once the rows are full. That way a pin stands before
// the labels it reaches past, not under them. A label that would run past the
// end of the line runs back from its pin instead. Takes every event, in view or
// not, so that moving along the line does not reshuffle the rows
export const stackEventLabels = (
  events: AnnualWheelEvent[],
  position: (date: Date) => number,
  labelWidth: (annualWheelEvent: AnnualWheelEvent) => number,
  lineEnd: number,
  maxRows: number,
) => {
  const rowStarts: number[] = [];
  const places = new Map<number, EventLabelPlace>();

  for (const annualWheelEvent of [...events].sort(byDayThenName).reverse()) {
    const tip = position(eventMidday(annualWheelEvent));
    const width = labelWidth(annualWheelEvent);
    const isFlipped = tip <= lineEnd && tip + width > lineEnd;
    const start = isFlipped ? tip - width : tip;
    const freeRow = rowStarts.findIndex(
      (rowStart) => start + width <= rowStart,
    );
    const row = freeRow === -1 ? rowStarts.length : freeRow;

    if (row >= maxRows) continue;

    rowStarts[row] = start;
    places.set(annualWheelEvent.id, { row, isFlipped });
  }

  return places;
};
